// §4.12 — THE TASK LIST, /sales/tasks (Module 09, P3-E PR-E2, D-292 ruling 1;
// P3-E2 PR E2-1, D-313, D-314).
//
// A manager's tasks: the ones they add by hand, and the ones the system
// creates when a customer could not be reached (a status message with no
// channel to go through, a customer who opted out) or has not acted after the
// last reminder (a ready order not collected, a quote nobody answered). Without
// a screen those system tasks would be rows nobody sees — a producer with no
// reader is a silent failure, which is why step 1 of activities shipped a list
// (D-292/1).
//
// WHO SEES IT. The people tasks are for — `sales.activity.write`: group_admin,
// facility_admin and sales. A task can be ASSIGNED only to a holder of that
// code (the server answers 422 ACTIVITY_ASSIGNEE_NOT_ELIGIBLE for anyone else,
// and the admin's picker offers no one else), so a planner or a finance
// reader, who holds `sales.read` alone, never has a task to see; an
// always-empty list in the nav is the disabled shell D-088 forbids.
//
// THE FILTERS LIVE IN THE ADDRESS (E2-1, spec 09 §4.12, D-314 ruling 6), so
// the list adopts saved views as `sales.tasks`, with My day and Overdue as
// system views, and PR-E2's `customerTouched` state retires as it did on
// §4.5. The grammar is `screens/tasks-search-params.ts`: `scope` (`all` only),
// `status` (`done` or `cancelled`; absent is open), `due`, `customer`,
// `assignee` and `order`, with `view` and `peek` beside them. Every filter and
// sort write goes through the views controller (`views.write`), which judges
// it against the active view and keeps `peek` (ruling 8).
//
// MINE / EVERYONE'S. `scope=mine` is the caller's own tasks; `scope=all` is
// every task the caller may READ. §4.12 offers the switch to a literal admin
// alone, and a non-admin arriving on an address or a shared view that says
// `all` reads their own tasks (hooks/activity-queries.ts `tasksListQuery`),
// because only a row read under `mine` can carry their verbs.
//
// THE VERBS (E2-1, D-313 rulings 1 and 2): Complete and Move to another day
// for the assignee or an admin, Edit on a manual task, Reassign for an admin,
// Withdraw on a manual task for an admin or the assignee who wrote it. Each is
// gated in components/sales-tasks.tsx and offered only where it would pass.
//
// THE DONE CELL (E2-2d, D-326 ruling 5). The Done column completes the task
// in place on Complete's own gate, and is read-only on a done row
// (components/sales-tasks.tsx `useDoneCellEditor`). Complete stays among the
// verbs, which is how a phone completes a task.
//
// THE PEEK (spec 09 §4.15, D-314 ruling 3). Each row's Peek opens its most
// specific subject beside the list, which is its order, else its quote, else
// its deal, else its customer, through the sales lists' one peek
// (components/peek/sales-peek.tsx). A row click keeps no meaning of its own
// here, and a task with no subject offers no Peek. The address carries the
// open peek (`peek=<kind>:<uuid>`), which no view stores and every view write
// keeps (ruling 8); below 769 px no Peek renders and an address carrying one
// lands on the record's own page (ruling 4).
//
// OPEN / DONE / CLOSED. The third view holds the tasks nobody completed: the
// system's automatic closes (the order was collected, the quote answered —
// D-292/4) and, since E2-1, the manual tasks a person withdrew, each row
// saying which.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesFilterBar } from "../components/sales-filter-bar";
import { SalesGroupTabs } from "../components/group-tabs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { SegmentedControl, Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { bookPager, useBookPages, useBookWindow } from "../hooks/book-pages";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useReadsHeldOffline } from "../lib/reads";
import { useSavedViews, ViewSwitcher, ViewsRegion } from "../../views";
import {
  NewTaskModal,
  TaskActionButtons,
  TaskAssignee,
  TaskClosedAt,
  TaskDue,
  TaskSubjectLinks,
  TaskWhat,
  ALL_TASK_VERBS,
  useDoneCellEditor,
  taskWhat,
  isTaskOverdue,
  taskOriginVariant,
  taskStatusKey,
  taskStatusVariant,
  useAssigneeCandidates,
  useTaskActions,
} from "../components/sales-tasks";
import { SalesPeek, taskPeekCode, useSalesPeek } from "../components/peek/sales-peek";
import { tasksListQuery, useActivities, type SalesActivity } from "../hooks/activity-queries";
import { canOpenTasks, isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { useCustomer360, useCustomers } from "../hooks/sales-queries";
import { compactSearch } from "./search-params";
import {
  TASK_DUE_TOKENS,
  TASKS_DEFAULT_ORDER,
  isTasksOrderToken,
  validateSalesTasksSearch,
  type SalesTasksSearch,
} from "./tasks-search-params";

/** The keys a column's caret rides on. `worklist` is the declared default
 * and no column's; `due_on` is the Due column's; `-updated_at` is reachable
 * through a stored view. */
type TasksSortKey = "worklist" | "due_on" | "updated_at";

/** The scopes the switch offers this reader — `all` to the admins only
 * (pure, unit-tested; §4.12). The server would answer anyone's `all` with
 * what they may read — the narrowing is not a refusal — so this is the
 * screen's choice of view, not a permission guard. */
export function offeredScopes(isAdmin: boolean): readonly ("mine" | "all")[] {
  return isAdmin ? ["mine", "all"] : ["mine"];
}

/** Does the address narrow the list by a FILTER (pure, unit-tested)? Scope
 * and status choose which list is read, and each has its own empty sentence;
 * `due`, `customer` and `assignee` narrow it, and an empty narrowed list asks
 * the reader to widen them. */
export function tasksFiltered(search: Pick<SalesTasksSearch, "due" | "customer" | "assignee">) {
  return search.due !== undefined || search.customer !== undefined || search.assignee !== undefined;
}

/** The address with every narrowing filter cleared and everything else kept
 * (pure, unit-tested): Clear filters leaves the scope, the status, the order,
 * the view and the peek where they were. */
export function withoutTaskFilters(search: SalesTasksSearch): SalesTasksSearch {
  return { ...search, due: undefined, customer: undefined, assignee: undefined };
}

export function SalesTasksScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();

  const allowed = canOpenTasks(perms);
  const refused = isSalesReadRefused(perms, allowed);

  // THE ADDRESS. The route runs the same validator, and the views controller
  // re-runs it on a stored view at apply.
  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const search = useMemo(() => validateSalesTasksSearch(rawSearch), [rawSearch]);
  const setSearch = (next: SalesTasksSearch) => {
    void navigate({ to: "/sales/tasks", search: compactSearch(next), replace: true });
  };
  const status = search.status ?? "open";
  const scopeShown = perms.isAdmin && search.scope === "all" ? "all" : "mine";
  const order = search.order ?? TASKS_DEFAULT_ORDER;
  const customerId = search.customer;

  const [customerText, setCustomerText] = useState("");
  const customerQ = useDebouncedValue(customerText.trim(), 250);
  const [creating, setCreating] = useState(false);

  const customerMatches = useCustomers(
    { q: customerQ },
    undefined,
    !refused && customerQ.length > 1,
  );
  const filteredCustomer = useCustomer360(customerId ?? "");
  const filteredCustomerName = filteredCustomer.data?.customer.display_name;
  // The admin's assignee picker (§4.12: "whose control only a literal admin
  // is shown"); the same member read as the create form's picker. Any other
  // reader is shown the control only while an assignee is in force (a shared
  // view or a link can carry one), holding it as "Not in this list", so no
  // list is narrowed by a filter its reader cannot see or clear (D-314).
  const assignees = useAssigneeCandidates(!refused && perms.isAdmin);
  const showAssignee = perms.isAdmin || search.assignee !== undefined;
  // A directory read the browser holds offline reports pending until the
  // network returns (lib/reads.ts), so it counts as settled while offline.
  const offline = useReadsHeldOffline();

  const query = useMemo(() => tasksListQuery(search, perms.isAdmin), [search, perms.isAdmin]);
  const queryKey = JSON.stringify(query);
  // One page at a time (D-560): the stack starts again at page 1 whenever
  // the query does.
  const pages = useBookPages(queryKey);
  const cursor = pages.cursor;

  // Under `mine` every row is the reader's own. Under `all` (read by an
  // admin alone) no row is proven theirs, and an admin needs no proof.
  const rowsAreOwn = query.scope === "mine";
  const actions = useTaskActions(ALL_TASK_VERBS, "sales-task-complete-failed");
  const doneEditor = useDoneCellEditor(() => rowsAreOwn, "sales-task-done-cell");
  const peek = useSalesPeek();

  // THE REVEAL GATE: the controller's verdict, carried in state and starting
  // closed, as on the order book. A passive observer of the list's own key
  // feeds the controller its rows before the live read is allowed to run.
  const [listGate, setListGate] = useState(false);
  const shown = useActivities(query, cursor, false);
  const shownData = listGate ? shown.data : undefined;
  const rows = useMemo(() => shownData?.items ?? [], [shownData]);
  const turning = listGate && shown.isPlaceholderData;
  const pageView = useBookWindow(pages, {
    items: rows,
    nextCursor: shownData?.next_cursor,
    turning,
    settled: shownData !== undefined && !shown.isFetching,
  });

  // WHAT leads: it is the phone card's title (the collapse contract makes the
  // first cell the unlabelled heading), and "Ready order not collected" is
  // the question a manager scans for; the subject links follow it.
  const columns: DataTableColumn<SalesActivity, TasksSortKey>[] = [
    {
      key: "what",
      header: t("sales.tasks.colWhat"),
      id: true,
      render: (row) => <TaskWhat row={row} />,
    },
    {
      key: "done",
      header: t("sales.cells.done.header"),
      editable: doneEditor,
    },
    {
      key: "subject",
      phonePriority: 1,
      header: t("sales.tasks.colSubject"),
      render: (row) => <TaskSubjectLinks row={row} />,
    },
    {
      key: "due_on",
      kind: "date",
      phonePriority: 2,
      header: t("sales.tasks.colDue"),
      sortKey: "due_on",
      render: (row) => <TaskDue row={row} />,
    },
    {
      key: "assignee",
      phonePriority: 3,
      header: t("sales.tasks.colAssignee"),
      render: (row) => <TaskAssignee row={row} />,
    },
    {
      // The list has no kind filter, and the column is hidden until a column
      // configuration shows it (§4.12).
      key: "kind",
      header: t("sales.tasks.colKind"),
      defaultHidden: true,
      render: (row) => (
        <span data-testid="sales-task-kind-cell">{t(`sales.tasks.kind.${row.kind}`)}</span>
      ),
    },
    {
      key: "origin",
      header: t("sales.tasks.colOrigin"),
      render: (row) => (
        <StatusChip
          variant={taskOriginVariant(row.origin)}
          dot={false}
          data-testid="sales-task-origin"
          data-origin={row.origin}
        >
          {t(`sales.tasks.origin.${row.origin}`)}
        </StatusChip>
      ),
    },
    {
      key: "status",
      kind: "status",
      header: t("sales.tasks.colStatus"),
      render: (row) => (
        <span>
          <StatusChip
            variant={taskStatusVariant(row.status)}
            data-testid="sales-task-status"
            data-status={row.status}
            data-cancel-reason={row.cancel_reason ?? undefined}
          >
            {t(taskStatusKey(row))}
          </StatusChip>
          <TaskClosedAt row={row} />
        </span>
      ),
    },
    {
      key: "actions",
      header: t("sales.tasks.colActions"),
      actions: true,
      render: (row) => (
        <TaskActionButtons
          row={row}
          isOwn={rowsAreOwn}
          actions={actions}
          testIdPrefix="sales-task"
        />
      ),
    },
  ];

  const views = useSavedViews<SalesTasksSearch, SalesActivity, TasksSortKey>({
    screenKey: "sales.tasks",
    search,
    setSearch,
    validate: validateSalesTasksSearch,
    columns,
    rows,
    rowsLoading: !listGate || shown.isFetching,
    // A refused stored filter is named in this bar's own words (V5).
    filterLabels: {
      scope: t("sales.tasks.scopeLabel"),
      status: t("sales.tasks.statusLabel"),
      due: t("sales.tasks.filterDue"),
      customer: t("sales.tasks.filterCustomer"),
      assignee: t("sales.tasks.filterAssignee"),
    },
  });
  if (listGate !== views.listEnabled) setListGate(views.listEnabled);

  const live = useActivities(query, cursor, !refused && listGate);
  const page = {
    data: listGate ? live.data : undefined,
    error: listGate ? live.error : null,
    isPending: !listGate || live.isPending,
    isFetching: listGate && live.isFetching,
    fetchStatus: listGate ? live.fetchStatus : "idle",
    refetch: live.refetch,
  };

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  /** Typing a NEW customer search abandons the filter in force, and the box
   * empties when anything but the picker moves the customer (the order
   * book's two rules). */
  const pickerWrote = useRef(customerId);
  useEffect(() => {
    if (customerId === pickerWrote.current) return;
    pickerWrote.current = customerId;
    setCustomerText("");
  }, [customerId]);
  const writeCustomer = (next: string | undefined) => {
    pickerWrote.current = next;
    views.write({ ...search, customer: next });
  };

  const setOrder = (next: string) => {
    if (!isTasksOrderToken(next)) return;
    views.write({ ...search, order: next === TASKS_DEFAULT_ORDER ? undefined : next });
  };

  const isFiltered = tasksFiltered(search);
  const customerOnly =
    customerId !== undefined && search.due === undefined && search.assignee === undefined;
  const clearFilters = () => {
    setCustomerText("");
    pickerWrote.current = undefined;
    views.write(withoutTaskFilters(search));
  };

  const emptyTitle = isFiltered
    ? customerOnly
      ? t("sales.tasks.emptyFilteredTitle")
      : t("sales.tasks.emptyFiltersTitle")
    : status === "open"
      ? t("sales.tasks.emptyOpenTitle")
      : status === "done"
        ? t("sales.tasks.emptyDoneTitle")
        : t("sales.tasks.emptyCancelledTitle");
  const emptyBody = isFiltered
    ? customerOnly
      ? t("sales.tasks.emptyFilteredBody")
      : t("sales.tasks.emptyFiltersBody")
    : status === "open"
      ? t("sales.tasks.emptyOpenBody")
      : status === "done"
        ? t("sales.tasks.emptyDoneBody")
        : t("sales.tasks.emptyCancelledBody");

  const paused = page.fetchStatus === "paused";
  const listSettled = page.data !== undefined || page.error !== null;
  // An admin's Assignee select lists the member directory, so the region waits
  // for it too; a failed directory read still opens it (the picker then holds
  // Anyone and the address's own member), and so does one the browser holds
  // offline, whose list then says the network is gone.
  const assigneesPending = assignees.isPending && !offline;
  const assigneesSettled = !perms.isAdmin || !assigneesPending;
  const ready = refused || (views.settled && (listSettled || paused) && assigneesSettled);
  const assigneeName = assignees.candidates.find((m) => m.id === search.assignee)?.name;

  const chipLabel = (filter: string, value: string): string =>
    t("sales.common.filterChip", { filter, value });

  return (
    <section className="sales-tasks-screen" data-testid="sales-tasks-screen">
      <PageHeader
        title={t("sales.tasks.title")}
        actions={
          <>
            {allowed ? (
              <Button
                variant="primary"
                data-testid="sales-task-new"
                onClick={() => {
                  setCreating(true);
                }}
              >
                {t("sales.tasks.new")}
              </Button>
            ) : null}
          </>
        }
      />
      <SalesGroupTabs group="tasks" />

      <ViewsRegion ready={ready} cols={columns.length}>
        {/* The filter row is part of the reveal: an admin's Assignee select
            is decided by the member directory, and painted before it the
            select widened and pushed every control after it (design-system
            §5). */}
        {refused ? null : (
          <SalesFilterBar
            data-testid="sales-tasks-filters"
            actions={
              isFiltered ? (
                <Button size="sm" data-testid="sales-tasks-clear" onClick={clearFilters}>
                  {t("sales.tasks.clearFilters")}
                </Button>
              ) : null
            }
            filters={[
              {
                // Open, Done or Closed works as the list's tab, so it is the
                // one control in the row. On a phone it moves into the sheet
                // with the rest and a chip says when it is not Open.
                key: "status",
                inline: true,
                applied:
                  status === "open"
                    ? undefined
                    : chipLabel(
                        t("sales.tasks.statusLabel"),
                        status === "done"
                          ? t("sales.tasks.statusDone")
                          : t("sales.tasks.statusCancelled"),
                      ),
                onClear: () => {
                  views.write({ ...search, status: undefined });
                },
                control: (
                  <SegmentedControl
                    label={t("sales.tasks.statusLabel")}
                    data-testid="sales-tasks-status"
                    value={status}
                    onChange={(next) => {
                      views.write({ ...search, status: next === "open" ? undefined : next });
                    }}
                    options={[
                      { value: "open", label: t("sales.tasks.statusOpen") },
                      { value: "done", label: t("sales.tasks.statusDone") },
                      { value: "cancelled", label: t("sales.tasks.statusCancelled") },
                    ]}
                  />
                ),
              },
              {
                key: "scope",
                applied:
                  scopeShown === "all"
                    ? chipLabel(t("sales.tasks.scopeLabel"), t("sales.tasks.scopeAll"))
                    : undefined,
                onClear: () => {
                  views.write({ ...search, scope: undefined });
                },
                control: perms.isAdmin ? (
                  <SegmentedControl
                    label={t("sales.tasks.scopeLabel")}
                    data-testid="sales-tasks-scope"
                    value={scopeShown}
                    onChange={(next) => {
                      views.write({ ...search, scope: next === "all" ? "all" : undefined });
                    }}
                    options={[
                      { value: "mine", label: t("sales.tasks.scopeMine") },
                      { value: "all", label: t("sales.tasks.scopeAll") },
                    ]}
                  />
                ) : null,
              },
              {
                key: "due",
                applied:
                  search.due === undefined
                    ? undefined
                    : chipLabel(t("sales.tasks.filterDue"), t(`sales.tasks.due.${search.due}`)),
                onClear: () => {
                  views.write({ ...search, due: undefined });
                },
                control: (
                  <Field label={t("sales.tasks.filterDue")}>
                    <Select
                      value={search.due ?? ""}
                      data-testid="sales-tasks-due"
                      onChange={(e) => {
                        views.write({
                          ...search,
                          due: TASK_DUE_TOKENS.find((token) => token === e.target.value),
                        });
                      }}
                    >
                      <option value="">{t("sales.tasks.dueAny")}</option>
                      {TASK_DUE_TOKENS.map((token) => (
                        <option key={token} value={token}>
                          {t(`sales.tasks.due.${token}`)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ),
              },
              {
                // Behind Filters. A chosen customer is named by its own line
                // under the bar (sales-tasks-customer-chip), so it is counted
                // on the button and gets no second chip here.
                key: "customer",
                applied:
                  customerId === undefined
                    ? undefined
                    : chipLabel(t("sales.tasks.filterCustomer"), filteredCustomerName ?? ""),
                control: (
                  <>
                    <Field label={t("sales.tasks.filterCustomer")}>
                      <input
                        value={customerText}
                        placeholder={t("sales.tasks.filterCustomerHint")}
                        data-testid="sales-tasks-customer-q"
                        onChange={(e) => {
                          setCustomerText(e.target.value);
                          if (customerId !== undefined) writeCustomer(undefined);
                        }}
                      />
                    </Field>
                    {customerQ.length > 1 ? (
                      <Field label={t("sales.tasks.filterCustomer")}>
                        <Select
                          value={customerId ?? ""}
                          data-testid="sales-tasks-customer"
                          onChange={(e) => {
                            writeCustomer(e.target.value.length > 0 ? e.target.value : undefined);
                          }}
                        >
                          <option value="">{t("sales.tasks.filterAll")}</option>
                          {(customerMatches.data?.items ?? []).map((customer) => (
                            <option key={customer.id} value={customer.id}>
                              {customer.display_name}
                            </option>
                          ))}
                        </Select>
                      </Field>
                    ) : null}
                  </>
                ),
              },
              {
                key: "assignee",
                applied:
                  search.assignee === undefined
                    ? undefined
                    : chipLabel(
                        t("sales.tasks.filterAssignee"),
                        assigneeName ?? t("sales.common.notListed"),
                      ),
                onClear: () => {
                  views.write({ ...search, assignee: undefined });
                },
                control: showAssignee ? (
                  <Field label={t("sales.tasks.filterAssignee")}>
                    <Select
                      value={search.assignee ?? ""}
                      data-testid="sales-tasks-assignee"
                      onChange={(e) => {
                        views.write({
                          ...search,
                          assignee: e.target.value.length > 0 ? e.target.value : undefined,
                        });
                      }}
                    >
                      <option value="">{t("sales.tasks.assigneeAny")}</option>
                      {search.assignee !== undefined && assigneeName === undefined ? (
                        <option value={search.assignee}>
                          {assigneesPending ? t("ref.resolving") : t("sales.common.notListed")}
                        </option>
                      ) : null}
                      {assignees.candidates.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null,
              },
            ]}
          />
        )}
        {!refused ? <ViewSwitcher views={views} /> : null}

        {!refused && customerId !== undefined ? (
          <div
            className="sales-toolbar"
            data-testid="sales-tasks-customer-chip"
            data-customer={customerId}
          >
            <span className="sales-hint">{t("sales.tasks.filterCustomer")}</span>
            <span data-testid="sales-tasks-customer-name">
              <RefLabel
                value={filteredCustomerName}
                id={customerId}
                pending={filteredCustomer.isPending}
              />
            </span>
            <Button
              size="sm"
              data-testid="sales-tasks-customer-clear"
              onClick={() => {
                setCustomerText("");
                writeCustomer(undefined);
              }}
            >
              {t("sales.common.filterCustomerClear")}
            </Button>
          </div>
        ) : null}

        {refused ? (
          <ForbiddenState title={t("sales.access.title")} body={t("sales.access.tasks")} />
        ) : null}

        {!refused && paused && rows.length === 0 ? (
          <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
        ) : null}

        {!refused && page.isPending && !paused && rows.length === 0 ? (
          <TableSkeleton rows={5} cols={columns.length} label={t("sales.tasks.loading")} />
        ) : null}

        {/* The error card — including a deploy-skew 404 while an old task still
            serves this hostname (plan risk 5): the screen says what failed and
            offers Retry, never a blank page. */}
        {!refused && page.error !== null && !(page.error instanceof UnauthorizedError) ? (
          <ErrorState
            title={t("sales.tasks.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(page.error)}
            onRetry={() => {
              void page.refetch();
            }}
          />
        ) : null}

        {actions.failure}

        {!refused && !page.isPending && !paused && page.error === null && rows.length === 0 ? (
          <EmptyState
            title={emptyTitle}
            body={emptyBody}
            ctaLabel={isFiltered ? t("sales.tasks.clearFilters") : undefined}
            onCta={isFiltered ? clearFilters : undefined}
          />
        ) : null}

        {!refused && rows.length > 0 ? (
          <>
            <DataTable
              pager={bookPager(t, i18n.language, pageView, {
                turning,
                onNext: () => {
                  const next = shownData?.next_cursor;
                  if (next === null || next === undefined) return;
                  pages.next(next, rows.length);
                },
                onPrev: () => {
                  pages.prev();
                },
              })}
              data-testid="sales-tasks-table"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              empty={null}
              caption={t("sales.tasks.tableCaption")}
              rowTestId="sales-task-row"
              rowProps={(row) => ({
                "data-task-id": row.id,
                "data-status": row.status,
                "data-origin": row.origin,
                "data-reason": row.reason_code ?? undefined,
                "data-cancel-reason": row.cancel_reason ?? undefined,
                "data-overdue": isTaskOverdue(row) ? "true" : "false",
              })}
              sticky
              columnConfig={views.columnConfig}
              peek={{
                key: (row) => taskPeekCode(row),
                current: peek.code,
                onOpen: (code) => {
                  peek.open(code);
                },
                onClose: peek.close,
                label: t("sales.peek.open"),
                rowLabel: (row) => t("sales.peek.openNamed", { name: taskWhat(t, row) }),
              }}
              sort={{
                value: order,
                onChange: setOrder,
                toggleLabel: (header: string, next: "ascending" | "descending") =>
                  next === "descending"
                    ? t("sales.orders.sortDesc", { column: header })
                    : t("sales.orders.sortAsc", { column: header }),
              }}
            />
          </>
        ) : null}
      </ViewsRegion>

      {actions.dialog}

      {/* The screen's one side peek, outside every transformed ancestor. */}
      <SalesPeek peek={peek} />

      {creating ? (
        <NewTaskModal
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </section>
  );
}
